import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './LegalPage.css';

function DisclaimerPage() {
  return (
    <div className="legal-page">
      <Navbar />

      <div className="legal-container">
        <div className="legal-header">
          <Link to="/" className="legal-back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M19 12H5m7-7l-7 7 7 7"/>
            </svg>
            Back to Home
          </Link>
          <h1>Disclaimer</h1>
          <p className="legal-updated">Last updated: March 2026</p>
        </div>

        <div className="legal-content">
          <div className="disclaimer-hero">
            <div className="disclaimer-icon-large">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h2>Important: Please Read Carefully</h2>
            <p>
              By using Mindmark, you acknowledge and agree to the following disclaimers.
              These disclaimers are essential to understanding the nature and limitations of our Service.
            </p>
          </div>

          <section className="legal-section">
            <div className="disclaimer-card">
              <h2>Not Legal Advice</h2>
              <p>
                Mindmark is an educational and assistive tool only. <strong>Nothing provided by
                Mindmark constitutes legal, professional, or regulatory advice.</strong>
              </p>
              <p>
                The information and analysis provided by the Service should not be relied upon as
                a substitute for consultation with qualified legal professionals, AHPRA compliance
                specialists, or other appropriate advisors.
              </p>
              <p>
                If you require legal advice regarding AHPRA compliance or advertising regulations,
                please consult with a qualified legal professional who specializes in healthcare
                regulatory matters.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <div className="disclaimer-card">
              <h2>No Guarantee of Compliance</h2>
              <p>
                <strong>Mindmark does not guarantee that your content complies with AHPRA
                regulations or any other regulatory requirements.</strong>
              </p>
              <p>
                The Service is designed to identify potential compliance issues, but it may not
                detect all issues. Additionally:
              </p>
              <ul>
                <li>AHPRA guidelines and regulations may change over time</li>
                <li>Interpretation of guidelines may vary</li>
                <li>Context-specific factors may affect compliance determinations</li>
                <li>The AI may misunderstand or misinterpret content</li>
              </ul>
              <p>
                <strong>You remain solely responsible for ensuring your content complies with all
                applicable regulations.</strong> A "compliant" result from Mindmark does not
                mean your content is guaranteed to be compliant with AHPRA or any other regulatory body.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <div className="disclaimer-card">
              <h2>AI Limitations</h2>
              <p>
                Mindmark uses artificial intelligence technology to analyze content. <strong>AI
                systems have inherent limitations:</strong>
              </p>
              <ul>
                <li>
                  <strong>Accuracy:</strong> The AI may produce inaccurate, incomplete, or misleading
                  results. It may flag content that is actually compliant or miss content that is
                  non-compliant.
                </li>
                <li>
                  <strong>Context:</strong> The AI may not fully understand the context in which
                  content is used, which can affect compliance determinations.
                </li>
                <li>
                  <strong>Currency:</strong> The AI's knowledge has a training cutoff date and may
                  not reflect the most recent regulatory changes.
                </li>
                <li>
                  <strong>Interpretation:</strong> The AI's interpretation of guidelines may differ
                  from official AHPRA interpretations.
                </li>
              </ul>
              <p>
                Always verify AI-generated results independently before making decisions based on them.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <div className="disclaimer-card">
              <h2>No Professional Relationship</h2>
              <p>
                Use of Mindmark does not create any professional relationship between you and
                Mindmark, its operators, or any affiliated parties. We do not:
              </p>
              <ul>
                <li>Provide legal representation</li>
                <li>Act as your compliance consultant</li>
                <li>Assume responsibility for your regulatory compliance</li>
                <li>Provide professional certification or approval of your content</li>
              </ul>
            </div>
          </section>

          <section className="legal-section">
            <div className="disclaimer-card">
              <h2>Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, <strong>Mindmark and its operators shall
                not be liable for any damages</strong> arising from:
              </p>
              <ul>
                <li>Reliance on results provided by the Service</li>
                <li>Regulatory fines, penalties, or sanctions</li>
                <li>Loss of professional registration or certification</li>
                <li>Damage to professional reputation</li>
                <li>Loss of business or revenue</li>
                <li>Any other direct, indirect, incidental, or consequential damages</li>
              </ul>
              <p>
                You use the Service at your own risk and agree to hold Mindmark harmless from
                any claims arising from your use of the Service.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <div className="disclaimer-card">
              <h2>Recommendations</h2>
              <p>To ensure compliance with AHPRA regulations, we recommend:</p>
              <ul>
                <li>
                  <strong>Use Mindmark as one tool among many</strong> in your compliance review
                  process, not as your sole source of guidance.
                </li>
                <li>
                  <strong>Consult with qualified professionals</strong> for definitive compliance
                  advice, especially for high-stakes content or novel situations.
                </li>
                <li>
                  <strong>Stay informed</strong> about AHPRA guidelines by regularly reviewing
                  official AHPRA publications and updates.
                </li>
                <li>
                  <strong>Document your compliance efforts</strong> to demonstrate due diligence
                  in meeting regulatory requirements.
                </li>
                <li>
                  <strong>When in doubt, err on the side of caution</strong> and seek professional
                  guidance before publishing content.
                </li>
              </ul>
            </div>
          </section>

          <section className="legal-section">
            <h2>Acknowledgment</h2>
            <p>
              By using Mindmark, you acknowledge that you have read, understood, and agree to
              this Disclaimer. You understand that Mindmark is an assistive tool only and that
              you bear full responsibility for ensuring your content complies with all applicable
              regulations.
            </p>
          </section>

          <section className="legal-section">
            <h2>Questions</h2>
            <p>
              If you have questions about this Disclaimer, please contact us at{' '}
              <a href="mailto:support@mindmark.au">support@mindmark.au</a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default DisclaimerPage;
